import { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Trash2,
  Key,
  AlertTriangle,
  X,
  Calendar,
  CheckCircle,
  Copy,
  Shield,
} from "lucide-react";
import type { APIKey, Team, Project } from "@/types/admin";
import adminService from "@/services/admin-service";
import { Card, CardBody } from "@/components/ui/Card";
import { cn } from "@/lib/utils";

export default function APIKeysPage() {
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [showRevoked, setShowRevoked] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    team_id: "",
    project_id: "",
    expires_in_days: 365,
  });

  useEffect(() => {
    loadAPIKeys();
  }, [showRevoked]);

  useEffect(() => {
    if (showCreateModal) {
      loadTeamsAndProjects();
    }
  }, [showCreateModal]);

  const loadTeamsAndProjects = async () => {
    try {
      const [teamsData, projectsData] = await Promise.all([
        adminService.listTeams(),
        adminService.listProjects(),
      ]);
      setTeams(teamsData || []);
      setProjects(projectsData || []);

      // Auto-select first team/project if available
      if (teamsData && teamsData.length > 0) {
        const firstTeamId = teamsData[0].team_id;
        setFormData((prev) => ({ ...prev, team_id: firstTeamId }));

        // Auto-select first project for the team if available
        const teamProjects = (projectsData || []).filter(
          (p) => p.team_id === firstTeamId,
        );
        if (teamProjects.length > 0) {
          setFormData((prev) => ({
            ...prev,
            project_id: teamProjects[0].project_id,
          }));
        }
      }
    } catch (err) {
      console.error("Failed to load teams/projects:", err);
    }
  };

  // Helper to get first project for a team
  const getFirstProjectForTeam = (teamId: string): string | null => {
    if (!teamId || !projects.length) return null;
    const teamProjects = projects.filter((p) => p.team_id === teamId);
    return teamProjects.length > 0 ? teamProjects[0].project_id : null;
  };

  const loadAPIKeys = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminService.listAPIKeys(undefined, showRevoked);
      setApiKeys(data || []); // Ensure we always have an array
    } catch (err) {
      setError("Failed to load API keys");
      console.error(err);
      setApiKeys([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateKey = async () => {
    if (!formData.name) {
      setError("Please provide a name for the API key");
      return;
    }

    if (!formData.team_id) {
      setError("Please select a team");
      return;
    }

    if (!formData.project_id) {
      setError("Please select a project");
      return;
    }

    try {
      setCreating(true);
      setError(null);

      // Build request - backend requires project_id
      const requestData = {
        project_id: formData.project_id,
        name: formData.name,
        expires_in_days: formData.expires_in_days,
      };

      const response = await adminService.createAPIKey(requestData);
      // Backend returns api_key field
      setGeneratedKey(response.api_key);
      await loadAPIKeys();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to generate API key");
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  const handleRotateKey = async (keyId: string, keyName: string) => {
    if (
      !confirm(
        `Rotate API key "${keyName}"? This will create a new key and revoke the old one. The new key will only be shown once.`,
      )
    ) {
      return;
    }

    try {
      setCreating(true);
      setError(null);

      const response = await adminService.rotateAPIKey(keyId);
      // Backend returns api_key field with new key
      setGeneratedKey(response.api_key);
      setShowCreateModal(true); // Show modal with new key
      await loadAPIKeys();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to rotate API key");
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  const handleRevokeKey = async (keyId: string, keyName: string) => {
    if (
      !confirm(
        `Are you sure you want to revoke API key "${keyName}"? This action cannot be undone.`,
      )
    ) {
      return;
    }

    try {
      setError(null);
      await adminService.revokeAPIKey(keyId);
      await loadAPIKeys();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to revoke API key");
      console.error(err);
    }
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setGeneratedKey(null);
    setFormData({
      name: "",
      team_id: "",
      project_id: "",
      expires_in_days: 365,
    });
    setError(null);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      // You could add a toast notification here
      alert("API key copied to clipboard!");
    });
  };

  const filteredKeys = (apiKeys || []).filter((key) =>
    key.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Enhanced Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 p-8 text-white">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative flex items-center justify-between">
          <div>
            <h1 className="mb-2 text-4xl font-bold">API Key Management</h1>
            <p className="text-lg text-purple-100">
              Securely manage API keys and access tokens for your projects
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden rounded-xl bg-white/10 px-4 py-2 backdrop-blur-sm sm:block">
              <p className="text-sm text-purple-100">Total Keys</p>
              <p className="text-2xl font-bold">{filteredKeys.length}</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-white/20 px-4 py-3 text-sm font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/30"
            >
              <Plus className="h-4 w-4" />
              Generate API Key
            </button>
          </div>
        </div>
        {/* Decorative elements */}
        <div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-white/10"></div>
        <div className="absolute -bottom-4 -right-12 h-32 w-32 rounded-full bg-white/5"></div>
      </div>

      {/* Error Display */}
      {error && (
        <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
          <CardBody>
            <div className="flex items-center space-x-3">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800 dark:text-red-200">
                  Error
                </p>
                <p className="text-sm text-red-600 dark:text-red-400">
                  {error}
                </p>
              </div>
              <button
                onClick={() => setError(null)}
                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Service Policy Warning */}
      <Card className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20">
        <CardBody>
          <div className="flex items-start space-x-3">
            <Shield className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="flex-1">
              <p className="mb-1 text-sm font-semibold text-amber-900 dark:text-amber-100">
                ⚠️ Service Policy Required for Service Accounts
              </p>
              <p className="mb-2 text-sm text-amber-700 dark:text-amber-300">
                API keys for service accounts (emails ending in{" "}
                <code className="rounded bg-amber-100 px-1.5 py-0.5 font-mono text-xs dark:bg-amber-800">
                  .gserviceaccount.com
                </code>
                ) <strong>require a Service Policy</strong> to work.
              </p>
              <p className="text-sm text-amber-700 dark:text-amber-300">
                Without a policy, all requests will be{" "}
                <strong>denied with 403 Forbidden</strong>. After creating an
                API key, go to{" "}
                <a
                  href="/admin/policies"
                  className="font-semibold underline hover:text-amber-900 dark:hover:text-amber-100"
                >
                  Service Policies
                </a>{" "}
                to configure access.
              </p>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Info: API Keys Cannot Be Retrieved */}
      {apiKeys.length > 0 && (
        <Card className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20">
          <CardBody>
            <div className="flex items-start space-x-3">
              <Key className="mt-0.5 h-5 w-5 text-blue-600 dark:text-blue-400" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  API Keys Cannot Be Retrieved After Creation
                </p>
                <p className="mt-1 text-sm text-blue-600 dark:text-blue-400">
                  For security reasons, full API keys are only shown once during
                  creation. If you've lost your key, you can{" "}
                  <strong>rotate</strong> it (creates a new key and revokes the
                  old one) using the rotate button in the actions column.
                </p>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Search and Filters */}
      <Card>
        <CardBody>
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div className="relative max-w-md flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search API keys by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-gray-50/50 py-3 pl-10 pr-4 text-sm placeholder-gray-400 transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white dark:placeholder-gray-500"
              />
            </div>
            <div className="flex items-center gap-3">
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={showRevoked}
                  onChange={(e) => setShowRevoked(e.target.checked)}
                  className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Show revoked keys
                </span>
              </label>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {filteredKeys.length} keys
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* API Keys Table */}
      <Card hover shadow="lg">
        <div className="overflow-hidden">
          <div className="scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                    API Key
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                    Key Value
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                    Created
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                    Expires
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                    Last Used
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white dark:divide-gray-700 dark:bg-gray-800">
                {filteredKeys.map((key, index) => (
                  <tr
                    key={key.key_id}
                    className="group transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  >
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center space-x-4">
                        <div
                          className={cn(
                            "flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r text-white shadow-lg",
                            index % 3 === 0 && "from-purple-500 to-indigo-600",
                            index % 3 === 1 && "from-blue-500 to-cyan-600",
                            index % 3 === 2 && "from-indigo-500 to-purple-600",
                          )}
                        >
                          <Key className="h-6 w-6" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-gray-900 dark:text-white">
                            {key.name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            API Access Key
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <code className="rounded-lg bg-gray-100 px-3 py-1 font-mono text-sm text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                        sk-••••••••••••••••••••••••••••••••••••{key.key_suffix}
                      </code>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <span
                        className={cn(
                          "inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold",
                          key.status === "active"
                            ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                            : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
                        )}
                      >
                        <div
                          className={cn(
                            "mr-2 h-2 w-2 rounded-full",
                            key.status === "active"
                              ? "bg-green-500"
                              : "bg-red-500",
                          )}
                        />
                        {key.status}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                        <Calendar className="h-4 w-4" />
                        <span>
                          {new Date(key.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {key.expires_at ? (
                          new Date(key.expires_at).toLocaleDateString()
                        ) : (
                          <span className="text-gray-400">Never</span>
                        )}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {key.last_used_at ? (
                          new Date(key.last_used_at).toLocaleDateString()
                        ) : (
                          <span className="text-gray-400">Never</span>
                        )}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2 opacity-0 transition-opacity group-hover:opacity-100">
                        {key.status === "active" && (
                          <button
                            onClick={() =>
                              handleRotateKey(key.key_id, key.name)
                            }
                            className="rounded-lg p-2 text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-700 dark:hover:bg-blue-900/20"
                            title="Rotate API key (creates new key, revokes old one)"
                          >
                            <Key className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleRevokeKey(key.key_id, key.name)}
                          className="rounded-lg p-2 text-red-600 transition-colors hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-900/20"
                          title="Revoke API key"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Card>

      {/* Enhanced Empty State */}
      {filteredKeys.length === 0 && (
        <Card>
          <CardBody>
            <div className="py-12 text-center">
              <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-purple-100 to-indigo-200 dark:from-purple-700 dark:to-indigo-600">
                <Key className="h-12 w-12 text-purple-600 dark:text-purple-300" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
                No API keys found
              </h3>
              <p className="mx-auto max-w-sm text-gray-500 dark:text-gray-400">
                {searchTerm
                  ? "Try adjusting your search term to find what you're looking for."
                  : "API keys allow secure access to your projects. Generate your first API key to get started."}
              </p>
              {!searchTerm ? (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="mt-6 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-lg transition-colors hover:from-purple-600 hover:to-indigo-700"
                >
                  <Plus className="h-4 w-4" />
                  Generate Your First API Key
                </button>
              ) : (
                <button
                  onClick={() => setSearchTerm("")}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
                >
                  Clear search
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Create API Key Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="relative mx-4 w-full max-w-lg rounded-2xl bg-white shadow-2xl dark:bg-gray-800">
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-gray-200 p-6 dark:border-gray-700">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {generatedKey ? "API Key Generated" : "Generate New API Key"}
              </h2>
              <button
                onClick={closeModal}
                className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {generatedKey ? (
                /* Success State - Show Generated Key */
                <div className="space-y-4">
                  <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
                    <CheckCircle className="mt-0.5 h-5 w-5 text-green-600 dark:text-green-400" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-green-800 dark:text-green-200">
                        API Key Generated Successfully
                      </p>
                      <p className="mt-1 text-sm text-green-600 dark:text-green-400">
                        ⚠️ This key will only be shown once. Store it securely!
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Your API Key
                    </label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 overflow-x-auto rounded-lg border border-gray-300 bg-gray-100 p-3 font-mono text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white">
                        {generatedKey}
                      </code>
                      <button
                        onClick={() => copyToClipboard(generatedKey)}
                        className="rounded-lg p-3 text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-700 dark:hover:bg-blue-900/20"
                        title="Copy to clipboard"
                      >
                        <Copy className="h-5 w-5" />
                      </button>
                    </div>
                  </div>

                  <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      <strong>Important:</strong> Store this API key securely in
                      your application's environment variables or secrets
                      manager. It cannot be retrieved again.
                    </p>
                  </div>

                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
                    <div className="flex items-start gap-2">
                      <Shield className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
                      <div className="flex-1">
                        <p className="mb-1 text-sm font-semibold text-amber-900 dark:text-amber-100">
                          Next Step: Create Service Policy
                        </p>
                        <p className="mb-2 text-sm text-amber-700 dark:text-amber-300">
                          This API key won't work until you create a{" "}
                          <strong>Service Policy</strong>. Service accounts
                          require explicit authorization.
                        </p>
                        <a
                          href="/admin/policies"
                          className="inline-flex items-center gap-2 rounded-lg bg-amber-100 px-3 py-1.5 text-sm font-medium text-amber-900 transition-colors hover:bg-amber-200 dark:bg-amber-800 dark:text-amber-100 dark:hover:bg-amber-700"
                        >
                          <Shield className="h-4 w-4" />
                          Configure Service Policy
                        </a>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={closeModal}
                    className="w-full rounded-xl bg-blue-600 px-4 py-3 font-semibold text-white transition-colors hover:bg-blue-700"
                  >
                    Done
                  </button>
                </div>
              ) : (
                /* Form State - Collect API Key Details */
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleGenerateKey();
                  }}
                  className="space-y-4"
                >
                  {/* Name Field */}
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Key Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      placeholder="e.g., Production API Key"
                      required
                      className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-3 text-sm placeholder-gray-400 transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white dark:placeholder-gray-500"
                    />
                  </div>

                  {/* Team Selection */}
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Team *
                    </label>
                    <select
                      value={formData.team_id}
                      onChange={(e) => {
                        const selectedTeamId = e.target.value;
                        // Auto-select first project for the team when team changes
                        const firstProjectId =
                          getFirstProjectForTeam(selectedTeamId);
                        setFormData({
                          ...formData,
                          team_id: selectedTeamId,
                          project_id: firstProjectId || "",
                        });
                      }}
                      required
                      className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-3 text-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white"
                    >
                      <option value="">Select a team...</option>
                      {teams.map((team) => (
                        <option key={team.team_id} value={team.team_id}>
                          {team.team_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Project Selection */}
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Project *
                    </label>
                    <select
                      value={formData.project_id}
                      onChange={(e) =>
                        setFormData({ ...formData, project_id: e.target.value })
                      }
                      required
                      className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-3 text-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white"
                    >
                      <option value="">Select a project...</option>
                      {projects
                        .filter((p) => p.team_id === formData.team_id)
                        .map((project) => (
                          <option
                            key={project.project_id}
                            value={project.project_id}
                          >
                            {project.project_name}
                          </option>
                        ))}
                    </select>
                    {formData.team_id &&
                      projects.filter((p) => p.team_id === formData.team_id)
                        .length === 0 && (
                        <p className="mt-1 text-xs text-yellow-600 dark:text-yellow-400">
                          No projects found for selected team. Please create a
                          project first.
                        </p>
                      )}
                  </div>

                  {/* Expiry Field */}
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Expires in (days)
                    </label>
                    <input
                      type="number"
                      value={formData.expires_in_days}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          expires_in_days: parseInt(e.target.value) || 365,
                        })
                      }
                      min="1"
                      max="3650"
                      className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-3 text-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Recommended: 365 days (1 year)
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      disabled={creating}
                      className="flex-1 rounded-xl bg-gray-100 px-4 py-3 font-semibold text-gray-700 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={
                        creating ||
                        !formData.name ||
                        !formData.team_id ||
                        !formData.project_id
                      }
                      className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {creating ? (
                        <>
                          <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                          Generating...
                        </>
                      ) : (
                        <>
                          <Key className="h-4 w-4" />
                          Generate API Key
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
